import type { InferUITool, tool, UIMessage, UITools } from "ai";
import { z } from "zod";
import type { AiUiStreamDataTypes } from "./ui-stream";
import {
	AiUiStreamIdDataSchema,
	AiUiStreamSourceDataSchema,
	AiUsageCostDataSchema,
} from "./ui-stream";

// Re-export ToolInputSchemas and ToolName from the single source of truth
export type { ToolInputSchemas, ToolName } from "./tool-input-schemas";

// ============================================================================
// AI SDK Type Re-exports (Centralized for Type Safety)
// ============================================================================

/**
 * Re-export core AI SDK types for convenience.
 * Import these from @event-manager/shared-types instead of 'ai' directly for consistency.
 *
 * Reference: ai-chatbot pattern (examples/ai-chatbot/lib/types.ts)
 */
export type {
	/** Data part type for custom data streaming */
	DataUIPart,
	/** File part type for file attachments */
	FileUIPart,
	/** Infer UI tool types from a single tool definition */
	InferUITool,
	/** Infer UI tool types from a toolset (ai-chatbot pattern: ChatTools = InferUITools<typeof tools>) */
	InferUITools,
	/** Reasoning part type for chain-of-thought */
	ReasoningUIPart,
	/** Text part type */
	TextUIPart,
	/** Base type for custom data parts */
	UIDataTypes,
	/** Core UIMessage type - source of truth for chat state */
	UIMessage,
	/** Stream chunk type for UIMessage streams */
	UIMessageChunk,
	/** Union of all UIMessage part types */
	UIMessagePart,
	/** Writer interface for UIMessage streams */
	UIMessageStreamWriter,
	/** Base type for tools collection */
	UITools,
} from "ai";

/**
 * UI Message role type - derived from UIMessage['role'].
 *
 * This is the AI SDK's role type for UIMessage ('user' | 'assistant' | 'system').
 * Use this for components that render AI SDK UIMessage objects.
 *
 * Note: This differs from the application's MessageRole (in common/enums.ts) which
 * includes additional roles like 'secretary' and 'admin' for application-specific needs.
 *
 * Reference: ai-chatbot pattern uses `UIMessage["role"]` for prop typing
 * in message components (Message, BranchSelector, etc.).
 *
 * @example
 * ```typescript
 * import type { UIMessageRole } from '@event-manager/shared-types'
 *
 * // Type-safe role prop for UI components rendering UIMessage
 * type MessageProps = {
 *   from: UIMessageRole
 * }
 *
 * // Conditional rendering based on role
 * const Message = ({ from }: MessageProps) => (
 *   <div className={from === 'user' ? 'user-style' : 'assistant-style'}>
 *     ...
 *   </div>
 * )
 * ```
 */
export type UIMessageRole = UIMessage["role"];

/**
 * Artifact kinds supported in this application.
 * We only support TEXT and SHEET artifacts.
 *
 * Using const array + type derivation pattern for type-safe iteration
 * and discriminated union support.
 */
export const ARTIFACT_KINDS = ["text", "sheet"] as const;
export const ArtifactKindSchema = z.enum(ARTIFACT_KINDS);

/**
 * Artifact kind type - derived from ARTIFACT_KINDS const array.
 * This ensures compile-time safety when iterating over artifact kinds.
 */
export type ArtifactKind = z.infer<typeof ArtifactKindSchema>;

/**
 * Type guard for ArtifactKind validation.
 * Use this to safely narrow unknown values to ArtifactKind.
 */
export function isArtifactKind(value: unknown): value is ArtifactKind {
	return (
		typeof value === "string" && ARTIFACT_KINDS.includes(value as ArtifactKind)
	);
}

/**
 * Message metadata (frontend UI needs extra identifiers for edit/cleanup flows).
 * Kept as a single shared type to prevent web/backend drift.
 */
export const messageMetadataSchema = z.object({
	createdAt: z.string(),
	persistedId: z.string().optional(),
	streamId: z.string().optional(),
	originalRole: z
		.enum(["user", "assistant", "secretary", "system", "tool", "admin"])
		.optional(),
	/** UI-only: marks message as finalized (stream complete, ready for persistence) */
	finalized: z.boolean().optional(),
	/**
	 * Full SDK-reported generation diagnostics (usage, cost, timing) for this
	 * message. Hydrated from the persisted `ConversationMessage.aiMetrics` on
	 * history load, or from the stream-end payload for live turns.
	 */
	aiMetrics: AiUsageCostDataSchema.optional(),
});

export type MessageMetadata = z.infer<typeof messageMetadataSchema>;

// ============================================================================
// Suggestion Type (for document edit suggestions)
// ============================================================================

/**
 * Suggestion data for document editing suggestions.
 *
 * Reference: ai-chatbot pattern - suggestions are streamed as data parts
 * when the AI suggests edits to documents/artifacts.
 *
 * @example
 * ```typescript
 * // Streaming a suggestion from the backend
 * dataStream.write({
 *   type: 'data-suggestion',
 *   data: {
 *     id: 'suggestion-123',
 *     documentId: 'doc-456',
 *     originalText: 'Hello world',
 *     suggestedText: 'Hello, World!',
 *     description: 'Capitalize and add comma for proper greeting',
 *   }
 * })
 * ```
 */
export const SuggestionSchema = z.object({
	createdAt: z.string().optional(),
	description: z.string().optional(),
	documentCreatedAt: z.string().optional(),
	documentId: z.string(),
	id: z.string(),
	isResolved: z.boolean().optional(),
	originalText: z.string(),
	suggestedText: z.string(),
	userId: z.string().optional(),
});

export type Suggestion = z.infer<typeof SuggestionSchema>;

// ============================================================================
// Custom UI Data Types
// ============================================================================

/**
 * Custom UI data types (aligned with AI SDK DataUIPart / data-* chunks).
 *
 * IMPORTANT: keys must match the suffix in `data-${key}` stream chunks.
 *
 * Reference: ai-chatbot pattern defines 12 custom data types:
 * - textDelta, imageDelta, sheetDelta, codeDelta: artifact content deltas
 * - suggestion: document edit suggestion
 * - appendMessage, id, title, kind, clear, finish, chat-title: control/metadata
 *
 * @example
 * ```typescript
 * // Streaming text delta (transient)
 * dataStream.write({ type: 'data-textDelta', data: 'Hello', transient: true })
 *
 * // Streaming code delta (transient)
 * dataStream.write({ type: 'data-codeDelta', data: 'const x = 1;', transient: true })
 *
 * // Streaming suggestion (persistent)
 * dataStream.write({ type: 'data-suggestion', data: suggestionObj })
 * ```
 */
export type CustomUIDataTypes = AiUiStreamDataTypes & {
	/** Text artifact content delta */
	textDelta: string;
	/** Image artifact content delta (base64 or URL) */
	imageDelta: string;
	/** Sheet artifact content delta (CSV format) */
	sheetDelta: string;
	/** Code artifact content delta */
	codeDelta: string;
	/** Document edit suggestion */
	suggestion: Suggestion;
	/** Append message to chat */
	appendMessage: string;
	/** Document/artifact ID */
	id: string;
	/** Document/artifact title */
	title: string;
	/** Chat title update */
	"chat-title": string;
	/** Artifact kind discriminator */
	kind: ArtifactKind;
	/** Clear artifact content */
	clear: null;
	/** Finish streaming */
	finish: null;
};

/** Runtime validators consumed by AI SDK v7 while processing UI data chunks. */
export const chatDataPartSchemas = {
	"stream-id": AiUiStreamIdDataSchema,
	usage: AiUsageCostDataSchema,
	source: AiUiStreamSourceDataSchema,
	textDelta: z.string(),
	imageDelta: z.string(),
	sheetDelta: z.string(),
	codeDelta: z.string(),
	suggestion: SuggestionSchema,
	appendMessage: z.string(),
	id: z.string(),
	title: z.string(),
	"chat-title": z.string(),
	kind: ArtifactKindSchema,
	clear: z.null(),
	finish: z.null(),
	// AI SDK v7 types schemas by data key, while the stream processor looks
	// them up using the serialized `data-${key}` chunk type. Keep both aliases
	// so validation runs at runtime without weakening the typed message map.
	"data-stream-id": AiUiStreamIdDataSchema,
	"data-usage": AiUsageCostDataSchema,
	"data-source": AiUiStreamSourceDataSchema,
	"data-textDelta": z.string(),
	"data-imageDelta": z.string(),
	"data-sheetDelta": z.string(),
	"data-codeDelta": z.string(),
	"data-suggestion": SuggestionSchema,
	"data-appendMessage": z.string(),
	"data-id": z.string(),
	"data-title": z.string(),
	"data-chat-title": z.string(),
	"data-kind": ArtifactKindSchema,
	"data-clear": z.null(),
	"data-finish": z.null(),
} satisfies Record<string, z.ZodType>;

// ============================================================================
// Typed Data Part Union (Reference: ai-chatbot UIMessageStreamWriter pattern)
// ============================================================================

/**
 * Union type for all typed data parts that can be written to the stream.
 *
 * This provides compile-time type safety for tool datastream writes,
 * matching the ai-chatbot reference pattern where tools write typed data parts:
 *
 * @example
 * ```typescript
 * // Reference pattern (ai-chatbot):
 * dataStream.write({ type: 'data-kind', data: kind, transient: true })
 * dataStream.write({ type: 'data-id', data: id, transient: true })
 * dataStream.write({ type: 'data-finish', data: null, transient: true })
 *
 * // Our typed implementation:
 * dataStream.write({ type: 'data-kind', data: 'text', transient: true })
 * dataStream.write({ type: 'data-id', data: 'doc-123', transient: true })
 * ```
 */
export type TypedDataPart<
	K extends keyof CustomUIDataTypes = keyof CustomUIDataTypes,
> = {
	[P in K]: {
		type: `data-${P & string}`;
		data: CustomUIDataTypes[P];
		transient?: boolean;
	};
}[K];

/**
 * Helper type to create a specific typed data part.
 *
 * @example
 * ```typescript
 * type KindPart = DataPartFor<'kind'>
 * // { type: 'data-kind', data: ArtifactKind, transient?: boolean }
 * ```
 */
export interface DataPartFor<K extends keyof CustomUIDataTypes> {
	data: CustomUIDataTypes[K];
	transient?: boolean;
	type: `data-${K & string}`;
}

/**
 * Create a typed data part for CustomUIDataTypes.
 *
 * This is the canonical helper for creating custom UI data parts with full type safety.
 * Use this for artifact-related data streaming (textDelta, codeDelta, suggestion, etc.).
 *
 * For base stream data parts (stream-id, usage, source), use
 * `createBaseDataPart` from ui-stream.
 *
 * @param key - The data key (without 'data-' prefix)
 * @param data - The payload matching the key's type
 * @param options - Optional settings like transient flag
 *
 * @example
 * ```typescript
 * import { createCustomDataPart } from '@event-manager/shared-types'
 *
 * // Create transient artifact metadata (not persisted)
 * const kindPart = createCustomDataPart('kind', 'text', { transient: true })
 * const idPart = createCustomDataPart('id', 'doc-123', { transient: true })
 * const finishPart = createCustomDataPart('finish', null, { transient: true })
 *
 * // Create code delta (transient)
 * const codePart = createCustomDataPart('codeDelta', 'const x = 1;', { transient: true })
 *
 * // Create suggestion (persistent)
 * const suggestionPart = createCustomDataPart('suggestion', {
 *   id: 'sug-1',
 *   documentId: 'doc-1',
 *   originalText: 'Hello',
 *   suggestedText: 'Hello, World!',
 * })
 * ```
 */
export function createCustomDataPart<K extends keyof CustomUIDataTypes>(
	key: K,
	data: CustomUIDataTypes[K],
	options?: { transient?: boolean }
): DataPartFor<K> {
	return {
		type: `data-${key as string}` as `data-${K & string}`,
		data,
		...(options?.transient === true ? { transient: true } : {}),
	};
}

// ============================================================================
// Tool Type Inference Utilities (AI SDK Pattern)
// ============================================================================

/**
 * Re-export InferUITool from AI SDK for consumers.
 * This type infers the UI-safe representation of a tool definition.
 *
 * @example
 * ```typescript
 * import { tool } from 'ai'
 *
 * const getWeather = tool({
 *   description: 'Get weather',
 *   inputSchema: z.object({ city: z.string() }),
 *   execute: async ({ city }) => ({ temp: 72 })
 * })
 *
 * // Infer the UI tool type
 * type WeatherTool = InferUIToolType<typeof getWeather>
 * ```
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type InferUIToolType<T extends ReturnType<typeof tool>> = InferUITool<T>;

/**
 * Type helper for inferring tool types from factory functions.
 * Use this when tools are created via factory functions that take dependencies.
 *
 * @example
 * ```typescript
 * const createDocumentTool = (deps: { db: Database }) => tool({...})
 *
 * // Infer the UI tool type from factory return
 * type DocumentTool = InferUIToolFromFactory<typeof createDocumentTool>
 * ```
 */
export type InferUIToolFromFactory<
	T extends (...args: never[]) => ReturnType<typeof tool>,
> = InferUITool<ReturnType<T>>;

/**
 * Create a typed tools object from a record of tool definitions.
 * This enables static type inference for all tools in the object.
 *
 * @example
 * ```typescript
 * const tools = {
 *   getWeather: getWeatherTool,
 *   createDocument: createDocumentTool(deps),
 * } as const
 *
 * type MyTools = InferToolsObject<typeof tools>
 * // { getWeather: InferUITool<...>, createDocument: InferUITool<...> }
 * ```
 */
export type InferToolsObject<
	T extends Record<string, ReturnType<typeof tool>>,
> = {
	[K in keyof T]: InferUITool<T[K]>;
};

/**
 * Base ChatTools type using AI SDK UITools.
 *
 * Tools are dynamically discovered at runtime; we use AI SDK `UITools` to avoid
 * drift between backend tool registry and frontend rendering.
 *
 * For static type inference of specific tools, create a custom ChatTools type:
 *
 * @example
 * ```typescript
 * import type { InferUITool } from 'ai'
 *
 * // When you have direct access to tool definitions:
 * type MyAppChatTools = {
 *   getWeather: InferUITool<typeof getWeatherTool>
 *   scheduleEvent: InferUITool<ReturnType<typeof createScheduleEventTool>>
 * }
 *
 * type MyAppChatMessage = UIMessage<MessageMetadata, CustomUIDataTypes, MyAppChatTools>
 *
 * // For runtime-discovered tools, use ToolInputSchemas for type hints:
 * import type { ToolInputSchemas } from '@event-manager/shared-types'
 * const input: ToolInputSchemas['scheduleEvent'] = { ... }
 * ```
 */
export type ChatTools = UITools;

// ============================================================================
// Chat Message Types
// ============================================================================

/**
 * Generic ChatMessage type with customizable type parameters.
 *
 * @template TMetadata - Message metadata type (default: MessageMetadata)
 * @template TDataTypes - Custom UI data types (default: CustomUIDataTypes)
 * @template TTools - Chat tools type (default: ChatTools)
 *
 * @example
 * ```typescript
 * // Use with defaults
 * type Message = ChatMessage
 *
 * // Use with custom tools type
 * type MyMessage = ChatMessage<MessageMetadata, CustomUIDataTypes, MyAppChatTools>
 * ```
 */
export type ChatMessage<
	TMetadata extends Record<string, unknown> = MessageMetadata,
	TDataTypes extends Record<string, unknown> = CustomUIDataTypes,
	TTools extends UITools = ChatTools,
> = UIMessage<TMetadata, TDataTypes, TTools>;

/**
 * Default ChatMessage type with standard parameters.
 * This is the recommended type for most use cases.
 */
export type DefaultChatMessage = UIMessage<
	MessageMetadata,
	CustomUIDataTypes,
	ChatTools
>;

// ============================================================================
// Tool Input/Output Type Inference
// ============================================================================

/**
 * Extract the input type from a tool definition.
 *
 * @example
 * ```typescript
 * const myTool = tool({
 *   inputSchema: z.object({ name: z.string() }),
 *   execute: async (input) => ({ result: input.name })
 * })
 *
 * type Input = ExtractToolInput<typeof myTool>
 * // { name: string }
 * ```
 */
export type ExtractToolInput<T> = T extends { inputSchema: z.ZodType<infer I> }
	? I
	: never;

/**
 * Extract the output type from a tool definition's execute function.
 *
 * @example
 * ```typescript
 * const myTool = tool({
 *   inputSchema: z.object({ name: z.string() }),
 *   execute: async (input) => ({ result: input.name })
 * })
 *
 * type Output = ExtractToolOutput<typeof myTool>
 * // { result: string }
 * ```
 */
export type ExtractToolOutput<T> = T extends {
	execute: (...args: never[]) => Promise<infer O>;
}
	? O
	: never;

// ============================================================================
// Chat Status Types (AI SDK useChat hook status)
// ============================================================================

/**
 * Chat status values returned by AI SDK's useChat hook.
 *
 * These represent the current state of the chat interaction:
 * - 'ready': Chat is idle and ready for user input
 * - 'submitted': User message has been submitted, waiting for response
 * - 'streaming': AI response is being streamed
 * - 'error': An error occurred during the chat interaction
 *
 * Reference: AI SDK v7 useChat status property
 * @see https://sdk.vercel.ai/docs/ai-sdk-ui/chatbot#status
 *
 * @example
 * ```typescript
 * import type { ChatStatus } from '@event-manager/shared-types'
 *
 * // Type-safe status handling
 * const renderStatus = (status: ChatStatus) => {
 *   switch (status) {
 *     case 'submitted':
 *       return <ThinkingMessage />
 *     case 'streaming':
 *       return <StreamingIndicator />
 *     case 'error':
 *       return <ErrorMessage />
 *     default:
 *       return null
 *   }
 * }
 * ```
 */
export const CHAT_STATUS = {
	READY: "ready",
	SUBMITTED: "submitted",
	STREAMING: "streaming",
	ERROR: "error",
} as const;

/**
 * Chat status type - union of all possible status values.
 * Derived from the CHAT_STATUS const object for type safety.
 */
export type ChatStatus = (typeof CHAT_STATUS)[keyof typeof CHAT_STATUS];

/**
 * Type guard to check if a value is a valid ChatStatus.
 *
 * @param value - The value to check
 * @returns true if the value is a valid ChatStatus
 *
 * @example
 * ```typescript
 * import { isChatStatus } from '@event-manager/shared-types'
 *
 * const status: unknown = 'streaming'
 * if (isChatStatus(status)) {
 *   // status is now typed as ChatStatus
 *   handleStatus(status)
 * }
 * ```
 */
export function isChatStatus(value: unknown): value is ChatStatus {
	return (
		typeof value === "string" &&
		Object.values(CHAT_STATUS).includes(value as ChatStatus)
	);
}

/**
 * Helper to check if chat is in a loading state (submitted or streaming).
 *
 * @param status - The current chat status
 * @returns true if the chat is loading (submitted or streaming)
 *
 * @example
 * ```typescript
 * import { isChatLoading } from '@event-manager/shared-types'
 *
 * const shouldShowThinking = isChatLoading(status) && !hasAssistantResponse
 * ```
 */
export function isChatLoading(status: ChatStatus): boolean {
	return status === CHAT_STATUS.SUBMITTED || status === CHAT_STATUS.STREAMING;
}

/**
 * Helper to check if chat is actively streaming a response.
 *
 * @param status - The current chat status
 * @returns true if the chat is streaming
 */
export function isChatStreaming(status: ChatStatus): boolean {
	return status === CHAT_STATUS.STREAMING;
}

/**
 * Helper to check if chat is ready for user input.
 *
 * @param status - The current chat status
 * @returns true if the chat is ready
 */
export function isChatReady(status: ChatStatus): boolean {
	return status === CHAT_STATUS.READY;
}

// ============================================================================
// Message Content Utilities
// ============================================================================

/**
 * Extract text content from a ChatMessage or UIMessage.
 *
 * This utility filters message parts for text parts and concatenates their content.
 * Useful for displaying message previews, generating summaries, or processing
 * message text content.
 *
 * Reference: ai-chatbot pattern (examples/ai-chatbot/lib/utils.ts)
 *
 * @param message - A ChatMessage or UIMessage with parts array
 * @returns Concatenated text content from all text parts
 *
 * @example
 * ```typescript
 * import { getTextFromMessage } from '@event-manager/shared-types'
 *
 * // Get text content from a message
 * const textContent = getTextFromMessage(message)
 *
 * // Use for preview
 * const preview = getTextFromMessage(message).slice(0, 100)
 *
 * // Check if message has text content
 * if (getTextFromMessage(message).length > 0) {
 *   // Process text content
 * }
 * ```
 */
export function getTextFromMessage(
	message:
		| ChatMessage
		| UIMessage
		| { parts: Array<{ type: string; text?: string }> }
): string {
	return message.parts
		.filter(
			(part): part is { type: "text"; text: string } => part.type === "text"
		)
		.map((part) => part.text)
		.join("");
}

/**
 * Get the most recent user message from an array of messages.
 *
 * Reference: ai-chatbot pattern (examples/ai-chatbot/lib/utils.ts)
 *
 * @param messages - Array of UIMessage or ChatMessage
 * @returns The most recent user message, or undefined if none found
 *
 * @example
 * ```typescript
 * import { getMostRecentUserMessage } from '@event-manager/shared-types'
 *
 * const userMessage = getMostRecentUserMessage(messages)
 * if (userMessage) {
 *   const text = getTextFromMessage(userMessage)
 *   // Process user's last message
 * }
 * ```
 */
export function getMostRecentUserMessage<T extends { role: string }>(
	messages: T[]
): T | undefined {
	const userMessages = messages.filter((message) => message.role === "user");
	return userMessages.at(-1);
}

/**
 * Sanitize text by removing function call markers.
 *
 * Reference: ai-chatbot pattern (examples/ai-chatbot/lib/utils.ts)
 *
 * @param text - Text to sanitize
 * @returns Sanitized text with function call markers removed
 */
export function sanitizeText(text: string): string {
	return text.replace("<has_function_call>", "");
}

// ============================================================================
// Tool Call Info/Result Types
// ============================================================================

/**
 * Schema for tool call information.
 * Contains metadata about a tool invocation.
 */
export const ToolCallInfoSchema = z.object({
	/** Unique identifier for the tool call */
	toolCallId: z.string(),
	/** Name of the tool being called */
	toolName: z.string(),
	/** Arguments passed to the tool */
	args: z.record(z.string(), z.unknown()).optional(),
});

/**
 * Tool call information type.
 * Contains metadata about a tool invocation.
 */
export type ToolCallInfo = z.infer<typeof ToolCallInfoSchema>;

/**
 * Schema for tool call result.
 * Contains the result of a tool invocation.
 */
export const ToolCallResultSchema = z.object({
	/** Unique identifier for the tool call */
	toolCallId: z.string(),
	/** Name of the tool that was called */
	toolName: z.string(),
	/** Arguments that were passed to the tool */
	args: z.record(z.string(), z.unknown()).optional(),
	/** Result returned by the tool */
	result: z.unknown().optional(),
	/** Error message if the tool call failed */
	error: z.string().optional(),
});

/**
 * Tool call result type.
 * Contains the result of a tool invocation.
 */
export type ToolCallResult = z.infer<typeof ToolCallResultSchema>;
